// WHICH PAGE READS WHICH PART OF THE STORE. A document load is seeded with
// these groups only (lib/server/store-seed.ts, from the path the proxy copies
// onto `x-ironside-url`), a client navigation into a page whose groups the tab
// does not hold loads them before the page renders (components/store-sync.tsx,
// components/store-group-gate.tsx), and the poll's diff covers the groups the
// tab holds.
//
// Until this table, every hard load carried the whole store, and 33 of the 42
// signed-in pages render nothing from it: the dashboards, the brand pages,
// onboarding, sourcing, settings, the guides. A pilot-era shortcut
// (poll everything, always) that outlived the pilot.
//
// ⚠️ SPARSE, LIKE lib/route-access.ts, AND READ THE SAME WAY: the deepest
// listed ancestor of a path decides, a "[segment]" matches any one segment,
// and a path nothing here lists reads no group. A line that restates its
// parent is a second place for the answer to live.
//
// ⚠️ A PAGE MISSING HERE RENDERS EMPTY, NOT BROKEN, which is the silent kind of
// failure: its stores are simply never filled. tests/unit/route-store-groups.test.ts
// walks every page's imports and fails on a page that reaches a store module
// with no line here naming its group. It cannot tell a store READ from a WRITE,
// so a page that only writes (the root layout's star widget; the editor's grade
// corrections) is named there as an exemption, with the reason.

import { STORE_GROUPS, type StoreGroup } from "./store-groups";

export const ROUTE_STORE_GROUPS: Readonly<Record<string, readonly StoreGroup[]>> = {
  // The two script pages and the schedule. The script store reads the schedule
  // store, so both script pages list it; the Schedule page reads nothing else.
  // Only the editor shows comments, Drive footage and undo points, so only it
  // waits for them.
  "/scripts/videos": ["scripts", "schedule"],
  "/scripts/editor": ["scripts", "schedule", "editor"],
  "/scripts/schedule": ["schedule"],

  // The SME correction rails: Script Grading's, the Weekly Report's two skill
  // pages', DM Replies' and client Slack's nine (each its own kind, one group).
  // Every skill page is under /skills, so the line is
  // the section's: the generic pages, which have no rail, are seeded the
  // comments too, a small group, rather than this table listing thirteen pages.
  "/skills": ["rubric"],

  // The feedback console lists all four of its stores: ratings, grader
  // feedback, grade corrections and the rails' comments.
  "/feedback": ["consoles", "rubric"],
  // A graded run's page and its correction rail. Not the Graded Runs list
  // (a tab of Script Grading's page), a server render over grade_run.
  "/admin/grades/[scriptId]": ["consoles"],
};

const PATTERNS = Object.keys(ROUTE_STORE_GROUPS).filter((k) => k.includes("["));

/** "/admin/grades/[scriptId]" matches "/admin/grades/JC-OI-W1-4": same depth, "[…]" is any one segment. */
function matchesPattern(pattern: string, path: string): boolean {
  const want = pattern.split("/");
  const got = path.split("/");
  if (want.length !== got.length) return false;
  return want.every((seg, i) => (seg.startsWith("[") && seg.endsWith("]") ? got[i] !== "" : seg === got[i]));
}

/**
 * The groups a page at this path reads: the deepest listed ancestor's, or none.
 * A query or hash on the path is ignored (`?as=`, `#video-3`).
 */
export function storeGroupsFor(path: string): readonly StoreGroup[] {
  const pathname = path.split(/[?#]/)[0];
  const parts = pathname.split("/").filter(Boolean);
  for (let i = parts.length; i > 0; i--) {
    const node = "/" + parts.slice(0, i).join("/");
    const listed = ROUTE_STORE_GROUPS[node];
    if (listed) return listed;
    const pattern = PATTERNS.find((p) => matchesPattern(p, node));
    if (pattern) return ROUTE_STORE_GROUPS[pattern];
  }
  return [];
}

/**
 * The groups a document load is seeded with, from the path the proxy copied
 * onto the request (lib/lens-url.ts URL_HEADER). ⚠️ EVERY GROUP WHEN THE PATH
 * IS MISSING, as carriesStoreSeed sends the seed when it cannot tell: a page
 * hydrating against stores it was never sent is the mismatch the seed exists
 * to prevent, and a few hundred KB too many is only the bytes this saves.
 */
export function seedGroupsFor(path: string | null): readonly StoreGroup[] {
  return path ? storeGroupsFor(path) : STORE_GROUPS;
}
